import { forwardRef, useState } from 'react'
import styled from 'styled-components'
import IconTextField from './IconTextField'
import BarcodeIcon from '../../icons/IconLib/BarcodeIcon'
import SearchIcon from '../../icons/IconLib/SearchIcon'
import { useBarcodeScanner } from '../../hooks/useBarcodeScanner'
import selectOnFocus from '../../../modules/autoselect'

export interface ScanBarProps {
  /** Fired on manual submit (Enter / search button) and on hardware-scanner reads. */
  onScan: (barcode: string) => void
  /** Controlled value. Omit to let ScanBar manage its own input state. */
  value?: string
  onChange?: (value: string) => void
  placeholder?: string
  disabled?: boolean
  tabIndex?: number
  className?: string
  /**
   * Capture hardware-scanner reads globally (window keydown/paste via useBarcodeScanner).
   * Disable when a parent already owns the scanning flow (e.g. its own scan endpoint),
   * so ScanBar stays a plain controlled input + submit.
   */
  captureGlobalScans?: boolean
}

/**
 * Reusable barcode search bar: a manual text field (Enter / button to submit)
 * optionally combined with the global hardware-scanner listener. Typed input is
 * handled by the field; physical-scanner reads (while the field is not focused)
 * are captured by useBarcodeScanner and forwarded to the same onScan.
 */
const ScanBar = forwardRef<HTMLInputElement, ScanBarProps>(
  (
    {
      onScan,
      value,
      onChange,
      placeholder = 'ברקוד',
      disabled,
      tabIndex,
      className,
      captureGlobalScans = true,
    },
    ref,
  ) => {
    const [internal, setInternal] = useState<string>('')
    const isControlled = value !== undefined
    const current = isControlled ? value : internal

    const setValue = (next: string) => {
      if (!isControlled) setInternal(next)
      onChange?.(next)
    }

    const handleSubmit = () => {
      if (disabled) return
      onScan(current)
    }

    useBarcodeScanner({
      onScan: (scanned) => {
        setValue(scanned)
        onScan(scanned)
      },
      enabled: captureGlobalScans && !disabled,
      ignoreFocusedInputs: true,
    })

    return (
      <IconTextField
        ref={ref}
        className={className}
        value={current}
        onChange={(e) => setValue(e.target.value)}
        onClick={selectOnFocus}
        placeholder={placeholder}
        disabled={disabled}
        tabIndex={tabIndex}
        enterKeyHint='search'
        leadingIcon={<BarcodeIcon />}
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleSubmit()
        }}
        endAdornment={
          <SearchBtn type='button' onClick={handleSubmit} disabled={disabled} aria-label='חפש'>
            <SearchIcon />
          </SearchBtn>
        }
      />
    )
  },
)

ScanBar.displayName = 'ScanBar'

export default ScanBar

const SearchBtn = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: 1px solid ${({ theme }) => theme.colors.accent};
  background: ${({ theme }) => theme.colors.accent};
  color: ${({ theme }) => theme.colors.ink};
  cursor: pointer;
  border-radius: 0.5rem;
  transition: all ${({ theme }) => theme.motion.fast} ease;

  svg {
    width: 1.125rem;
    height: 1.125rem;
  }

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.accentDeep};
    border-color: ${({ theme }) => theme.colors.accentDeep};
  }

  &:active:not(:disabled) {
    transform: scale(0.96);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`
